<script>
import api from "../axios.js";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;

export default {
  data() {
    return {
      student: {
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        dateOfBirth: "",
        school: "",
        program: "",
        graduationYear: "",
        linkedIn: "",
        cv: null,
        interestedCompanies: [],
      },
      appliedContactMeetings: [],
      originalValues: {},
      unsavedFields: new Set(),
      showSnackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      birthYears: [],
      graduationYears: [],
      fileInputModel: null,
      showPendingDialog: false,
      selectedPendingMeeting: null,
    };
  },
  created() {
    this.birthYears = this.generateBirthYears();
    this.graduationYears = this.generateGraduationYears();
    this.fetchStudent();
    this.fetchAppliedContactMeetings();
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  methods: {
    generateBirthYears() {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 83 }, (_, i) => {
        const year = currentYear - 17 - i;
        return { text: year.toString(), value: year };
      });
    },
    generateGraduationYears() {
      const currentYear = new Date().getFullYear();
      const years = Array.from({ length: 6 }, (_, i) => {
        const year = currentYear + i;
        return { text: year.toString(), value: year };
      });
      years.push({
        text: `${currentYear + 6} ->`,
        value: `${currentYear + 6} ->`,
      });
      return years;
    },
    async fetchAppliedContactMeetings() {
      //KANSKE GÖRA EN GEMENSAM FUNKTION SOM HÄMTAR ALL STUDENTINFORMATION SAMTIDIGT
      const store = useStore();

      try {
        const res = await api.get(
          `${apiBaseUrl}/contact-meetings/${store.state.user.user}/2026-11-10`,
          {
            headers: {
              Authorization: `Bearer ${store.state.token}`,
            },
          },
        );
        this.appliedContactMeetings = res.data;
      } catch (err) {
        console.error("Could not get users booked times:", err);
      }
    },
    async fetchStudent() {
      const store = useStore();
      try {
        await store.dispatch("student/fetchStudent");
        this.student = store.state.student.student;
        this.originalValues = { ...this.student };
      } catch (err) {
        this.showNotification(
          this.t("errors.fetchProfile") + " " + this.t("errors.signOutAndIn"),
          "error",
        );
      }
    },
    markUnsaved(field) {
      if (this.student[field] !== this.originalValues[field]) {
        this.unsavedFields.add(field);
      } else {
        this.unsavedFields.delete(field);
      }
    },
    cancelField(field) {
      this.student[field] = this.originalValues[field];
      if (field === "cv") {
        this.fileInputModel = null; // rensa fil-inputen också
      }
      this.unsavedFields.delete(field);
    },
    cancelAll() {
      for (const field of this.unsavedFields) {
        this.student[field] = this.originalValues[field];
        if (field === "cv") {
          this.fileInputModel = null; // rensa fil-inputen
        }
      }
      this.unsavedFields.clear();
    },
    async saveAll() {
      if (this.unsavedFields.size === 0) {
        return;
      }

      const formData = new FormData();

      this.unsavedFields.forEach((field) => {
        const value = this.student[field];

        if (field === "cv") {
          if (value instanceof File) {
            formData.append("cv", value);
          } else if (value === null) {
            formData.append("cv", "");
          }
        } else {
          formData.append(field, value);
        }
      });

      try {
        await api.put(`${apiBaseUrl}/students/me`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        // Uppdatera cv i frontend för att visa rätt filnamn
        if (this.unsavedFields.has("cv") && this.student.cv instanceof File) {
          this.student.cv = {
            fileName: this.student.cv.name,
            url: null,
          };
        }

        for (const field of this.unsavedFields) {
          this.originalValues[field] = this.student[field];
        }

        this.unsavedFields.clear();
        this.showNotification(this.t("success.profileUpdated"), "success");
      } catch (err) {
        console.error("Save failed:", err);
        this.showNotification(this.t("errors.saveField"), "error");
      }
    },
    showNotification(message, type) {
      this.snackbarMessage = message;
      this.snackbarColor = type;
      this.showSnackbar = true;
    },
    formatFieldValue(key, value) {
      if (key === "school") {
        return value
          ? this.t(`profile.schools.${value}`)
          : this.t("common.notSelected");
      } else if (key === "program") {
        return value
          ? this.t(`profile.programs.${value}`)
          : this.t("common.notSelected");
      } else if (key === "graduationYear" || key === "dateOfBirth") {
        return value || this.t("common.notSelected");
      } else {
        return value;
      }
    },
    onFileChange(event) {
      const selectedFile = event?.target?.files?.[0];
      if (selectedFile) {
        this.fileInputModel = selectedFile;
        this.student.cv = selectedFile;
        this.markUnsaved("cv");
      }
    },
    onClearFileInput() {
      // När användaren klickar på clear-knappen i fil-inputen
      // Vi rensar bara fileInputModel men behåller student.cv (serverfil)
      this.fileInputModel = null;
      // Ingen ändring på student.cv -> filen kvar att ladda ner
    },
    removeExistingFile() {
      // Sätt student.cv till null (fil borttagen)
      this.student.cv = null;
      this.fileInputModel = null;
      this.markUnsaved("cv");
    },
    openPendingDialog(meeting) {
      this.selectedPendingMeeting = meeting;
      this.showPendingDialog = true;
    },
    async downloadCV(cv) {
      try {
        const res = await api.get(`${apiBaseUrl}/students/cv`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: { path: cv.path },
        });

        window.open(res.data.url, "_blank");
      } catch (err) {
        console.error(err);
        this.showNotification("Misslyckades att hämta CV", "error");
      }
    },
    parseDateTime(dateString, timeRange) {
      if (!dateString || !timeRange) return new Date(0);

      // plocka starttid ur intervallet
      const [start] = timeRange.split("-"); // "13:45"
      const [hour, minute] = start.split(":").map(Number);

      // skapa ett Date-objekt från ISO-strängen
      const date = new Date(dateString);

      // sätt rätt timmar & minuter
      date.setHours(hour, minute, 0, 0);

      return date;
    },
  },
  computed: {
    filteredStudentFields() {
      const orderedFields = [
        "firstname",
        "lastname",
        "email",
        "phone",
        "dateOfBirth",
        "school",
        "program",
        "graduationYear",
        "linkedIn",
        "cv",
      ];
      return orderedFields.map((key) => ({
        key,
        value: this.student[key],
      }));
    },
    schoolOptions() {
      const schoolKeys = ["sh", "other"];
      return schoolKeys.map((key) => ({
        value: key,
        text: this.t(`profile.schools.${key}`),
      }));
    },
    programOptions() {
      const programKeys = [
        "economics",
        "interEconomics",
        "tourism",
        "artCultureEconomy",
        "logisticsEconomy",
        "econTechDesign",
        "managementIT",
        "entrepreneurship",
        "europeanStudies",
        "ppeProgram",
        "interculturalBusiness",
        "sportManagement",
        "journalism",
        "teacher",
        "otherProgram",
        "course",
      ];
      return programKeys.map((key) => ({
        value: key,
        text: this.t(`profile.programs.${key}`),
      }));
    },
    sortedBookedMeetings() {
      return this.appliedContactMeetings
        .filter((meeting) => meeting.booked === true)
        .sort(
          (a, b) =>
            this.parseDateTime(a.date, a.time) -
            this.parseDateTime(b.date, b.time),
        );
    },
    sortedAppliedMeetingsByCompany() {
      const grouped = {};

      this.appliedContactMeetings
        .filter((meeting) => meeting.booked === false)
        .forEach((meeting) => {
          if (!grouped[meeting.companyName]) {
            grouped[meeting.companyName] = [];
          }
          grouped[meeting.companyName].push(meeting);
        });

      // Sortera varje grupp för sig efter datum + tid
      Object.keys(grouped).forEach((company) => {
        grouped[company].sort(
          (a, b) =>
            this.parseDateTime(a.date, a.time) -
            this.parseDateTime(b.date, b.time),
        );
      });

      return grouped;
    },
    sortedInterestedCompanies() {
      if (!this.student?.interestedCompanies) return [];
      return [...this.student.interestedCompanies].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    },
  },
};
</script>

<template>
  <v-container class="mx-auto mt-16" style="max-width: 600px">
    <h2>{{ t("profile.title") }}</h2>
    <h3 class="text-h6">
      {{ t("profile.info") }}
    </h3>

    <v-row
      v-for="{ key } in filteredStudentFields"
      :key="key"
      class="mb-4 align-center"
      no-gutters
    >
      <v-col v-if="key === 'cv'" cols="11" sm="8" class="font-weight-bold">
        {{ t(`profile.${key}`) }} (PDF)
      </v-col>
      <v-col v-else cols="11" sm="8" class="font-weight-bold">
        {{ t(`profile.${key}`) }}
      </v-col>

      <v-col cols="11" sm="8" class="d-flex align-center gap-2">
        <!-- Födelseår -->
        <v-select
          v-if="key === 'dateOfBirth'"
          v-model="student.dateOfBirth"
          :items="birthYears"
          :ref="`${key}Input`"
          @update:model-value="markUnsaved(key)"
          dense
          clearable
          hide-details
          item-title="text"
          item-value="text"
          color="primary"
        />

        <!-- Skola -->
        <v-select
          v-else-if="key === 'school'"
          v-model="student.school"
          :items="schoolOptions"
          :ref="`${key}Input`"
          @update:model-value="markUnsaved(key)"
          item-title="text"
          item-value="value"
          dense
          clearable
          hide-details
          color="primary"
        />

        <!-- Program -->
        <v-select
          v-else-if="key === 'program'"
          v-model="student.program"
          :items="programOptions"
          :ref="`${key}Input`"
          @update:model-value="markUnsaved(key)"
          item-title="text"
          item-value="value"
          dense
          clearable
          hide-details
          color="primary"
        />

        <!-- Examensår -->
        <v-select
          v-else-if="key === 'graduationYear'"
          v-model="student.graduationYear"
          :items="graduationYears"
          :ref="`${key}Input`"
          @update:model-value="markUnsaved(key)"
          item-title="text"
          item-value="text"
          dense
          clearable
          hide-details
          color="primary"
        />

        <!-- Visa CV-ikon + filnamn -->
        <v-row
          v-else-if="key === 'cv'"
          class="align-center"
          style="cursor: pointer"
        >
          <v-col cols="12">
            <v-file-input
              v-model="fileInputModel"
              :label="t('profile.uploadCV') + ' (PDF)'"
              accept=".pdf"
              prepend-icon="mdi-upload"
              @change="onFileChange"
              @click:clear="onClearFileInput"
              hide-details
              dense
            />
          </v-col>

          <v-col v-if="student.cv" @click="downloadCV(student.cv)" cols="8">
            <v-row dense>
              <v-col cols="1" class="mr-2">
                <v-icon left>mdi-file-pdf-box</v-icon>
              </v-col>
              <v-col cols="10">{{ t("company.downloadCv") }}:</v-col>
              <v-col cols="12">{{
                student.cv?.fileName || t("profile.uploadCV")
              }}</v-col>
            </v-row>
          </v-col>

          <v-col cols="4">
            <v-btn
              color="error"
              text
              small
              v-if="student.cv"
              @click="removeExistingFile"
            >
              {{ t("common.remove") }}
            </v-btn>
          </v-col>
        </v-row>

        <!-- Textfält -->
        <v-text-field
          v-else
          v-model="student[key]"
          :ref="`${key}Input`"
          @input="markUnsaved(key)"
          @update:model-value="markUnsaved(key)"
          dense
          :clearable="key !== 'email'"
          hide-details
          :readonly="key === 'email'"
          :append-inner-icon="key === 'email' ? '' : 'mdi-pencil'"
          :class="key !== 'email' ? 'cursor-pointer' : ''"
        />
      </v-col>
      <v-col cols="auto" class="ml-4">
        <!-- Avbryt per fält -->
        <v-btn v-if="unsavedFields.has(key)" dense @click="cancelField(key)">
          {{ t("common.cancel") }}
        </v-btn></v-col
      >
      <!-- Osparade ändringar -->
      <v-col cols="8" v-if="unsavedFields.has(key)">
        <em class="text-caption text-grey">
          {{ t("profile.unsavedChanges") }}
        </em>
      </v-col>
    </v-row>

    <!-- Åtgärdsknappar -->
    <v-row class="mt-4" v-if="unsavedFields.size > 0">
      <v-col cols="6">
        <v-btn color="primary" @click="saveAll">
          {{ t("common.saveAll") }}
        </v-btn>
      </v-col>
      <v-col cols="6">
        <v-btn color="error" variant="outlined" @click="cancelAll">
          {{ t("common.cancelAll") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="6" class="mt-4"
        ><v-btn
          ><router-link to="/change-password">{{
            t("login.changePassword")
          }}</router-link></v-btn
        >
      </v-col>
    </v-row>
    <v-row class="mt-8">
      <v-col cols="12">
        <h3>{{ t("profile.bookedContactMeetings") }}</h3>

        <v-list v-if="sortedBookedMeetings.length > 0">
          <v-list-item
            v-for="meeting in sortedBookedMeetings"
            :key="meeting._id"
            class="mb-4"
          >
            <v-list-item-title class="font-weight-bold">{{
              meeting.companyName
            }}</v-list-item-title>
            <v-row dense>
              <v-col cols="3">
                {{ meeting.time }}
              </v-col>
              <v-col cols="9">
                {{ new Date(meeting.date).toLocaleDateString("sv-SE") }}
              </v-col>
              <v-col>{{
                t("company.meetingRoom") + ": " + meeting.meetingRoom
              }}</v-col>
            </v-row>
          </v-list-item>
        </v-list>
        <p v-else class="text-grey">{{ t("profile.noBookedMeetings") }}</p>
      </v-col>
    </v-row>

    <v-row class="mt-8">
      <v-col cols="12">
        <h3>{{ t("profile.appliedContactMeetings") }}</h3>

        <v-list v-if="appliedContactMeetings.length">
          <div
            v-for="(meetings, companyName) in sortedAppliedMeetingsByCompany"
            :key="companyName"
            class="mb-6"
          >
            <p class="font-weight-bold ml-2">
              {{ companyName }}
            </p>
            <v-list-item
              v-for="meeting in meetings"
              :key="meeting._id"
              class="mb-2"
            >
              <v-list-item-title>{{ meeting.companyName }}</v-list-item-title>
              <v-row class="py-2">
                <!-- Tid -->
                <v-col cols="3" md="3">
                  {{ meeting.time }}
                </v-col>

                <!-- Datum -->
                <v-col cols="9" md="3">
                  {{ new Date(meeting.date).toLocaleDateString("sv-SE") }}
                </v-col>

                <!-- Pending info på NY rad i mobil (cols=12), men i samma rad på desktop (md=6) -->
                <v-col
                  cols="12"
                  md="6"
                  v-if="meeting.pending"
                  class="d-flex align-center cursor-pointer mt-n4 mt-md-0"
                  @click="openPendingDialog(meeting)"
                >
                  <v-icon small class="mr-2" color="error"
                    >mdi-information</v-icon
                  >
                  <span>{{ t("profile.bookingUnavailable") }}</span>
                </v-col>

                <!-- Separator under varje möte -->
                <v-col cols="12">
                  <v-divider />
                </v-col>
              </v-row>
            </v-list-item>
          </div>
        </v-list>
        <p v-else class="text-grey">{{ t("profile.noAppliedMeetings") }}</p>
      </v-col>
    </v-row>

    <v-row class="mt-8">
      <v-col cols="12">
        <h3>{{ t("profile.interestedCompanies") }}</h3>

        <v-list v-if="sortedInterestedCompanies.length">
          <v-list-item
            v-for="company in sortedInterestedCompanies"
            :key="company._id"
          >
            <v-list-item-title>{{ company.name }}</v-list-item-title>
          </v-list-item>
        </v-list>

        <p v-else class="text-grey">
          {{ t("profile.noInterestedCompanies") }}
        </p>
      </v-col>
    </v-row>

    <v-dialog v-model="showPendingDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">
          {{ selectedPendingMeeting?.companyName }}
        </v-card-title>
        <v-card-text>
          <p>
            <strong>{{ t("common.time") }}:</strong>
            {{ selectedPendingMeeting?.time }}
          </p>
          <p>
            <strong>{{ t("common.date") }}:</strong>
            {{
              new Date(selectedPendingMeeting?.date).toLocaleDateString("sv-SE")
            }}
          </p>
          <p class="mt-4">{{ t("common.either") }}:</p>
          <ul>
            <li>
              {{
                t("profile.sameTimeBooking") +
                " (" +
                selectedPendingMeeting?.time +
                " " +
                new Date(selectedPendingMeeting?.date).toLocaleDateString(
                  "sv-SE",
                ) +
                ")"
              }}
            </li>
            <li>
              {{
                t("profile.sameCompanyBooking") +
                " " +
                selectedPendingMeeting?.companyName
              }}
            </li>
            <li>
              {{
                t("profile.otherPersonBooking") +
                " " +
                selectedPendingMeeting?.time +
                " " +
                new Date(selectedPendingMeeting?.date).toLocaleDateString(
                  "sv-SE",
                ) +
                " " +
                t("profile.with") +
                " " +
                selectedPendingMeeting?.companyName
              }}
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="showPendingDialog = false">
            {{ t("common.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      timeout="5000"
      location="top"
      vertical
      elevation="10"
      class="mt-10"
    >
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.gap-2 {
  gap: 0.5rem;
}
</style>
